<script lang="ts">
  import { board, filters, isPhone } from "../lib/stores";
  import {
    enumerateDays,
    formatLongDate,
    formatRelativeTime,
    parseISODate,
    toISO,
  } from "../lib/date";
  import { getBestDays, summarizeDay } from "../lib/vote";
  import { t, localeTag, voteShort } from "../lib/i18n";

  const rangeDays = $derived(enumerateDays($filters.rangeFrom, $filters.rangeTo));

  // midnight today — "strongest days" proposes days to meet on, so days that
  // have already been and gone are excluded even when the range starts earlier
  // (which it does by default: the range opens on the 1st of the current month)
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  // On a phone this panel sits *above* the calendar (nothing after an infinite
  // scroller is reachable), so it shows fewer rows and scrolls with the page
  // rather than opening a nested scroll area under the reader's thumb.
  const bestDays = $derived(
    getBestDays(rangeDays, $board.votes, todayStart).slice(0, $isPhone ? 5 : 8),
  );
  const recentChanges = $derived($board.changes.slice(0, $isPhone ? 6 : 14));

  const stats = $derived.by(() => {
    let bestYes = 0;
    let voted = 0;
    for (const day of rangeDays) {
      const s = summarizeDay($board.votes, toISO(day));
      if (s.yes > bestYes) bestYes = s.yes;
      if (s.total > 0) voted += 1;
    }
    return { people: $board.members.length, bestYes, voted };
  });

  // Collapsed state is phone-only; the wide layout has a column to spare and
  // always shows both lists.
  let bestOpen = $state(true);
  let changesOpen = $state(false);
</script>

{#snippet bestBody()}
  {#if bestDays.length === 0}
    <p class="muted">{$t("side.noVotes")}</p>
  {:else}
    <div class="best-list">
      {#each bestDays as d (d.iso)}
        <div class="best">
          <strong>{formatLongDate(d.date, $localeTag)}</strong>
          <span
            >{$t("side.summary", {
              yes: d.summary.yes,
              maybe: d.summary.maybe,
              no: d.summary.no,
            })}</span
          >
        </div>
      {/each}
    </div>
  {/if}
{/snippet}

{#snippet changesBody()}
  {#if recentChanges.length === 0}
    <p class="muted">{$t("side.noChanges")}</p>
  {:else}
    <div class="changes">
      {#each recentChanges as c (c.id)}
        <div class="change">
          <div class="row">
            <strong>{c.userName}</strong>
            <span class="muted">{formatRelativeTime(c.at, $localeTag)}</span>
          </div>
          <div class="muted small">{formatLongDate(parseISODate(c.date), $localeTag)}</div>
          <div class="votes">
            <span class="tag {c.previousVote ?? 'none'}"
              >{c.previousVote ? $voteShort[c.previousVote] : "—"}</span
            >
            <span class="arrow">→</span>
            <span class="tag {c.nextVote ?? 'none'}"
              >{c.nextVote ? $voteShort[c.nextVote] : "—"}</span
            >
          </div>
        </div>
      {/each}
    </div>
  {/if}
{/snippet}

<aside class="sidebar">
  <div class="stats">
    <div class="stat panel"><span>{$t("side.people")}</span><strong>{stats.people}</strong></div>
    <div class="stat panel pos"><span>{$t("side.bestYes")}</span><strong>{stats.bestYes}</strong></div>
    <div class="stat panel"><span>{$t("side.votedDays")}</span><strong>{stats.voted}</strong></div>
  </div>

  <section class="panel block">
    {#if $isPhone}
      <button class="disclosure" onclick={() => (bestOpen = !bestOpen)} aria-expanded={bestOpen}>
        <h3>{$t("side.strongest")}</h3>
        <span class="n">{bestDays.length}</span>
        <span class="chev" class:up={bestOpen} aria-hidden="true">⌄</span>
      </button>
      {#if bestOpen}{@render bestBody()}{/if}
    {:else}
      <h3>{$t("side.strongest")}</h3>
      {@render bestBody()}
    {/if}
  </section>

  <section class="panel block">
    {#if $isPhone}
      <button
        class="disclosure"
        onclick={() => (changesOpen = !changesOpen)}
        aria-expanded={changesOpen}
      >
        <h3>{$t("side.recentChanges")}</h3>
        <span class="n">{recentChanges.length}</span>
        <span class="chev" class:up={changesOpen} aria-hidden="true">⌄</span>
      </button>
      {#if changesOpen}{@render changesBody()}{/if}
    {:else}
      <h3>{$t("side.recentChanges")}</h3>
      {@render changesBody()}
    {/if}
  </section>
</aside>

<style>
  .sidebar {
    display: grid;
    gap: 14px;
    align-content: start;
  }
  /* auto-fit rather than a rigid 3 columns: the tiles reflow instead of
     squashing their numbers when the sidebar drops under the calendar */
  .stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(84px, 1fr));
    gap: 10px;
  }
  .stat {
    padding: 14px;
    min-width: 0;
  }
  .stat span {
    display: block;
    color: var(--muted);
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    /* labels wrap onto a second line rather than being clipped to "VOTED …" */
    overflow-wrap: anywhere;
    hyphens: auto;
  }
  .stat strong {
    display: block;
    margin-top: 6px;
    font-size: clamp(22px, 4vw, 30px);
    letter-spacing: -0.04em;
  }
  .stat.pos strong {
    color: var(--yes-ink);
  }
  .block {
    padding: 16px;
  }
  .block h3 {
    font-size: 16px;
    margin-bottom: 12px;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
  .best-list,
  .changes {
    display: grid;
    gap: 8px;
    max-height: 320px;
    overflow: auto;
  }
  .best {
    display: grid;
    gap: 3px;
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: var(--surface);
  }
  .best strong {
    font-size: 14px;
  }
  .best span {
    color: var(--muted);
    font-size: 12px;
  }
  .change {
    display: grid;
    gap: 5px;
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: var(--surface);
  }
  .change .row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 13px;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .votes {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .tag {
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 900;
  }
  .tag.yes {
    color: var(--yes-ink);
    background: var(--yes-soft);
  }
  .tag.maybe {
    color: var(--maybe-ink);
    background: var(--maybe-soft);
  }
  .tag.no {
    color: var(--no-ink);
    background: var(--no-soft);
  }
  .tag.none {
    color: var(--muted);
    background: var(--empty);
  }
  .arrow {
    color: var(--muted);
    font-weight: 900;
  }

  /* phone disclosure header — same shape as the filters panel's */
  .disclosure {
    display: grid;
    grid-template-columns: 1fr auto auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: var(--tap);
    margin: -4px 0 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--ink);
    text-align: left;
  }
  .disclosure h3 {
    margin: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .disclosure .n {
    display: inline-grid;
    place-items: center;
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    border-radius: 999px;
    background: var(--chip);
    color: var(--ink);
    font-size: 12.5px;
    font-weight: 900;
  }
  .chev {
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
    color: var(--muted);
    transition: transform 0.18s;
  }
  .chev.up {
    transform: rotate(180deg);
  }

  @media (max-width: 760px) {
    .sidebar {
      gap: 10px;
    }
    .stats {
      gap: 8px;
    }
    .stat {
      padding: 11px 12px;
    }
    .stat span {
      font-size: 12.5px;
      letter-spacing: 0.02em;
    }
    .stat strong {
      margin-top: 2px;
      font-size: 26px;
    }
    .block {
      padding: 12px 14px;
    }
    /* the page already scrolls — a nested scroller here traps the thumb */
    .best-list,
    .changes {
      max-height: none;
      overflow: visible;
      margin-top: 10px;
    }
    .best strong,
    .change .row {
      font-size: 15px;
    }
    .best span,
    .small {
      font-size: 13px;
    }
    .tag {
      font-size: 12.5px;
      padding: 3px 10px;
    }
  }
</style>
